import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import './ImportEventPage.css';

const EditEventPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(UserContext);

  // Loading state for fetching event
  const [pageLoading, setPageLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  // Form state
  const [eventLink, setEventLink] = useState('');
  const [eventName, setEventName] = useState('');
  const [eventSeries, setEventSeries] = useState('');
  const [edition, setEdition] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [description, setDescription] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [venueName, setVenueName] = useState('');
  const [venueAddress, setVenueAddress] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const [eventImageUrl, setEventImageUrl] = useState('');
  const [selectedOrganizers, setSelectedOrganizers] = useState([]);
  const [organizerInput, setOrganizerInput] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [ticketLink, setTicketLink] = useState('');
  const [peopleResponse, setPeopleResponse] = useState('');

  // Currency options
  const currencyOptions = [
    { code: 'EUR', symbol: '\u20ac', label: 'EUR (\u20ac)' },
    { code: 'USD', symbol: '$', label: 'USD ($)' },
    { code: 'GBP', symbol: '\u00a3', label: 'GBP (\u00a3)' },
    { code: 'CHF', symbol: 'CHF', label: 'CHF' },
    { code: 'PLN', symbol: 'z\u0142', label: 'PLN (z\u0142)' },
    { code: 'CZK', symbol: 'K\u010d', label: 'CZK (K\u010d)' },
    { code: 'SEK', symbol: 'kr', label: 'SEK (kr)' },
    { code: 'NOK', symbol: 'kr', label: 'NOK (kr)' },
    { code: 'DKK', symbol: 'kr', label: 'DKK (kr)' },
    { code: 'CAD', symbol: 'C$', label: 'CAD (C$)' },
    { code: 'AUD', symbol: 'A$', label: 'AUD (A$)' },
    { code: 'BRL', symbol: 'R$', label: 'BRL (R$)' },
    { code: 'MXN', symbol: 'MX$', label: 'MXN (MX$)' },
  ];

  // Tags state
  const [selectedDanceStyles, setSelectedDanceStyles] = useState([]);
  const [selectedArtists, setSelectedArtists] = useState([]);
  const [danceStyleInput, setDanceStyleInput] = useState('');
  const [artistInput, setArtistInput] = useState('');

  // Autocomplete suggestions state
  const [danceStyleSuggestions, setDanceStyleSuggestions] = useState([]);
  const [artistSuggestions, setArtistSuggestions] = useState([]);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [venueSuggestions, setVenueSuggestions] = useState([]);
  const [organizerSuggestions, setOrganizerSuggestions] = useState([]);
  const [eventSeriesSuggestions, setEventSeriesSuggestions] = useState([]);

  // UI state
  const [statusMessage, setStatusMessage] = useState({ text: '', type: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wordCount, setWordCount] = useState(0);

  // URL validation state
  const [urlErrors, setUrlErrors] = useState({
    eventLink: null,
    ticketLink: null,
    imageUrl: null,
  });

  // Timeouts for debouncing
  const danceStyleTimeout = useRef(null);
  const artistTimeout = useRef(null);
  const locationTimeout = useRef(null);
  const venueTimeout = useRef(null);
  const organizerTimeout = useRef(null);
  const eventSeriesTimeout = useRef(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

  // URL validation helpers
  const isValidUrl = (url) => {
    if (!url || !url.trim()) return true;
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const validateUrl = (url, fieldName) => {
    if (!url || !url.trim()) return null;
    if (!isValidUrl(url)) {
      return `Please enter a valid URL for ${fieldName}. URLs should start with http:// or https://`;
    }
    return null;
  };

  // Dance style helpers
  const normalizeDanceStyle = (style) => {
    if (!style) return '';
    return style.trim().toLowerCase().split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const isDanceStyleSelected = (style, currentStyles) => {
    const normalizedNew = style.toLowerCase().trim();
    return currentStyles.some(existing => existing.toLowerCase() === normalizedNew);
  };

  const getToken = () => localStorage.getItem('token');

  // Format date for input[type=date]
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toISOString().split('T')[0];
  };

  // Fetch event data on mount
  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchEvent = async () => {
      try {
        const response = await fetch(`${API_BASE}/events/single-events/${id}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await response.json();

        if (data.success && data.data?.event) {
          const event = data.data.event;
          setEventName(event.title || '');
          setEventLink(event.eventlink === 'manual-entry' ? '' : event.eventlink || '');
          setEventSeries(event.eventSeries || '');
          setEdition(event.edition ? String(event.edition) : '');
          setStartDate(formatDateForInput(event.startDateTime));
          setEndDate(formatDateForInput(event.endDateTime));
          setDescription(event.description || '');
          setVenueName(event.location || '');
          setVenueAddress(event.venueAddress || '');
          setCity(event.city || '');
          setCountry(event.country || '');
          setCoordinates(event.coordinates || null);
          setEventImageUrl(event.eventImage || '');
          setSelectedOrganizers(event.organizers || (event.organizer ? [event.organizer] : []));
          setPrice(event.price || '');
          setCurrency(event.currency || 'EUR');
          setTicketLink(event.ticketLink || '');
          setPeopleResponse(event.peopleResponse || '');
          setSelectedDanceStyles(event.category || []);
          setSelectedArtists(event.artists || []);
          setLocationSearch([event.location, event.city, event.country].filter(Boolean).join(', '));
        } else {
          setFetchError(data.message || 'Event not found');
        }
      } catch (error) {
        console.error('Error fetching event:', error);
        setFetchError('Failed to load event data');
      } finally {
        setPageLoading(false);
      }
    };

    fetchEvent();
  }, [id, API_BASE]);

  // Update word count
  useEffect(() => {
    const words = description.trim().split(/\s+/).filter(word => word.length > 0);
    setWordCount(words.length);
  }, [description]);

  // Dance style autocomplete
  const handleDanceStyleInput = (e) => {
    const value = e.target.value;
    setDanceStyleInput(value);
    if (danceStyleTimeout.current) clearTimeout(danceStyleTimeout.current);
    if (value.trim().length < 1) { setDanceStyleSuggestions([]); return; }
    danceStyleTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE}/events/dance-styles?search=${encodeURIComponent(value)}`);
        const data = await response.json();
        if (data.success && data.data) setDanceStyleSuggestions(data.data);
      } catch (error) { console.error('Error fetching dance styles:', error); }
    }, 200);
  };

  const handleDanceStyleKeydown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = danceStyleInput.trim();
      if (value) {
        const items = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
        const newStyles = items.filter(item => !isDanceStyleSelected(item, selectedDanceStyles)).map(item => normalizeDanceStyle(item));
        if (newStyles.length > 0) setSelectedDanceStyles([...selectedDanceStyles, ...newStyles]);
        setDanceStyleInput(''); setDanceStyleSuggestions([]);
      }
    }
  };

  const addDanceStyle = (style) => {
    if (style && !isDanceStyleSelected(style, selectedDanceStyles)) {
      setSelectedDanceStyles([...selectedDanceStyles, normalizeDanceStyle(style)]);
      setDanceStyleInput(''); setDanceStyleSuggestions([]);
    }
  };

  const removeDanceStyle = (style) => setSelectedDanceStyles(selectedDanceStyles.filter(s => s !== style));

  // Artist autocomplete
  const handleArtistInput = (e) => {
    const value = e.target.value;
    setArtistInput(value);
    if (artistTimeout.current) clearTimeout(artistTimeout.current);
    if (value.trim().length < 1) { setArtistSuggestions([]); return; }
    artistTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE}/events/artists?search=${encodeURIComponent(value)}`);
        const data = await response.json();
        if (data.success && data.data) setArtistSuggestions(data.data);
      } catch (error) { console.error('Error fetching artists:', error); }
    }, 200);
  };

  const handleArtistKeydown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = artistInput.trim();
      if (value) {
        const items = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
        const newArtists = items.filter(item => !selectedArtists.includes(item));
        if (newArtists.length > 0) setSelectedArtists([...selectedArtists, ...newArtists]);
        setArtistInput(''); setArtistSuggestions([]);
      }
    }
  };

  const addArtist = (artist) => {
    if (artist && !selectedArtists.includes(artist)) {
      setSelectedArtists([...selectedArtists, artist]);
      setArtistInput(''); setArtistSuggestions([]);
    }
  };

  const removeArtist = (artist) => setSelectedArtists(selectedArtists.filter(a => a !== artist));

  // Location search
  const handleLocationInput = (e) => {
    const value = e.target.value;
    setLocationSearch(value);
    if (locationTimeout.current) clearTimeout(locationTimeout.current);
    if (value.trim().length < 3) { setLocationSuggestions([]); return; }
    locationTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&addressdetails=1&limit=8`,
          { headers: { 'Accept-Language': 'en', 'User-Agent': 'MovenMuse/1.0' } }
        );
        const data = await response.json();
        setLocationSuggestions(data);
      } catch (error) { console.error('Error fetching locations:', error); }
    }, 400);
  };

  const selectLocation = (loc) => {
    const address = loc.address || {};
    setLocationSearch(loc.display_name);
    setCity(address.city || address.town || address.village || address.municipality || address.county || '');
    setCountry(address.country || '');
    setCoordinates({ lat: parseFloat(loc.lat), lng: parseFloat(loc.lon) });
    const venueFromLocation = loc.name || address.hotel || address.venue || address.building || '';
    if (venueFromLocation && !venueName) setVenueName(venueFromLocation);
    if (!venueAddress) {
      const parts = [];
      if (address.road) parts.push(address.road);
      if (address.house_number && parts.length > 0) parts[0] = address.house_number + ' ' + parts[0];
      if (address.postcode) parts.push(address.postcode);
      setVenueAddress(parts.join(', '));
    }
    setLocationSuggestions([]);
  };

  // Venue autocomplete
  const handleVenueInput = (e) => {
    const value = e.target.value;
    setVenueName(value);
    if (venueTimeout.current) clearTimeout(venueTimeout.current);
    if (value.trim().length < 2) { setVenueSuggestions([]); return; }
    venueTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE}/events/venues?search=${encodeURIComponent(value)}`);
        const data = await response.json();
        if (data.success && data.data) setVenueSuggestions(data.data);
      } catch (error) { console.error('Error fetching venues:', error); }
    }, 300);
  };

  const selectVenue = (venue) => {
    setVenueName(venue.name);
    if (venue.address) setVenueAddress(venue.address);
    if (venue.city) setCity(venue.city);
    if (venue.country) setCountry(venue.country);
    setVenueSuggestions([]);
  };

  // Organizer autocomplete
  const handleOrganizerInput = (e) => {
    const value = e.target.value;
    setOrganizerInput(value);
    if (organizerTimeout.current) clearTimeout(organizerTimeout.current);
    if (value.trim().length < 2) { setOrganizerSuggestions([]); return; }
    organizerTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE}/events/organizers?search=${encodeURIComponent(value)}`);
        const data = await response.json();
        if (data.success && data.data) setOrganizerSuggestions(data.data);
      } catch (error) { console.error('Error fetching organizers:', error); }
    }, 200);
  };

  const handleOrganizerKeydown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = organizerInput.trim();
      if (value) {
        const items = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
        const newOrganizers = items.filter(item => !selectedOrganizers.includes(item));
        if (newOrganizers.length > 0) setSelectedOrganizers([...selectedOrganizers, ...newOrganizers]);
        setOrganizerInput(''); setOrganizerSuggestions([]);
      }
    }
  };

  const addOrganizer = (name) => {
    if (name && !selectedOrganizers.includes(name)) {
      setSelectedOrganizers([...selectedOrganizers, name]);
      setOrganizerInput(''); setOrganizerSuggestions([]);
    }
  };

  const removeOrganizer = (name) => setSelectedOrganizers(selectedOrganizers.filter(o => o !== name));

  // Event series autocomplete
  const handleEventSeriesInput = (e) => {
    const value = e.target.value;
    setEventSeries(value);
    if (eventSeriesTimeout.current) clearTimeout(eventSeriesTimeout.current);
    if (value.trim().length < 2) { setEventSeriesSuggestions([]); return; }
    eventSeriesTimeout.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_BASE}/events/event-series?search=${encodeURIComponent(value)}`);
        const data = await response.json();
        if (data.success && data.data) setEventSeriesSuggestions(data.data);
      } catch (error) { console.error('Error fetching event series:', error); }
    }, 200);
  };

  const selectEventSeries = (name) => { setEventSeries(name); setEventSeriesSuggestions([]); };

  // Image URL handler
  const handleImageUrlChange = (e) => {
    const value = e.target.value;
    setEventImageUrl(value);
    if (value && !isValidUrl(value)) {
      setUrlErrors(prev => ({ ...prev, imageUrl: 'Please enter a valid URL' }));
    } else {
      setUrlErrors(prev => ({ ...prev, imageUrl: null }));
    }
  };

  // Form submission - PUT update
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      setStatusMessage({ text: 'Please sign in to edit events.', type: 'error' });
      return;
    }

    setIsSubmitting(true);

    if (!eventName || !startDate || !endDate || !description || !venueName || !city || !country) {
      setStatusMessage({ text: 'Please fill in all required fields.', type: 'error' });
      setIsSubmitting(false);
      return;
    }

    if (selectedOrganizers.length === 0) {
      setStatusMessage({ text: 'Please add at least one organizer.', type: 'error' });
      setIsSubmitting(false);
      return;
    }

    if (selectedDanceStyles.length === 0) {
      setStatusMessage({ text: 'Please add at least one dance style.', type: 'error' });
      setIsSubmitting(false);
      return;
    }

    if (wordCount < 20) {
      setStatusMessage({ text: `Description must be at least 20 words. Currently: ${wordCount} words.`, type: 'error' });
      setIsSubmitting(false);
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      setStatusMessage({ text: 'End date cannot be before the start date.', type: 'error' });
      setIsSubmitting(false);
      return;
    }

    // Validate URLs
    if (eventLink && eventLink.trim()) {
      const err = validateUrl(eventLink, 'Event Link');
      if (err) { setStatusMessage({ text: err, type: 'error' }); setIsSubmitting(false); return; }
    }
    if (ticketLink && ticketLink.trim()) {
      const err = validateUrl(ticketLink, 'Ticket Link');
      if (err) { setStatusMessage({ text: err, type: 'error' }); setIsSubmitting(false); return; }
    }
    if (eventImageUrl && eventImageUrl.trim()) {
      const err = validateUrl(eventImageUrl, 'Image URL');
      if (err) { setStatusMessage({ text: err, type: 'error' }); setIsSubmitting(false); return; }
    }

    const eventData = {
      title: (eventName || '').trim(),
      eventlink: (eventLink || '').trim() || 'manual-entry',
      startDateTime: new Date(startDate + 'T00:00:00').toISOString(),
      endDateTime: new Date(endDate + 'T23:59:59').toISOString(),
      description: (description || '').trim(),
      location: (venueName || '').trim(),
      venueAddress: (venueAddress || '').trim(),
      city: (city || '').trim(),
      country: (country || '').trim(),
      category: selectedDanceStyles,
      eventImage: (eventImageUrl || '').trim(),
      organizers: selectedOrganizers,
      organizer: selectedOrganizers[0] || '',
      ticketLink: (ticketLink || '').trim() || undefined,
      price: (price || '').trim(),
      currency: currency,
      peopleResponse: (peopleResponse || '').trim(),
      artists: selectedArtists,
      eventSeries: (eventSeries || '').trim() || null,
      edition: edition ? parseInt(edition, 10) : null,
    };

    try {
      const response = await fetch(`${API_BASE}/events/update/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(eventData)
      });

      const data = await response.json();

      if (data.success) {
        setStatusMessage({ text: 'Event updated successfully! Redirecting...', type: 'success' });
        setTimeout(() => navigate(`/event/${id}`), 1500);
      } else {
        setStatusMessage({ text: data.message || 'Failed to update event. Please try again.', type: 'error' });
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error updating event:', error);
      setStatusMessage({ text: 'Something went wrong. Please try again.', type: 'error' });
      setIsSubmitting(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="import-event-page">
        <section className="page-header">
          <div className="container">
            <div className="page-badge">EDIT EVENT</div>
            <h1 className="page-title">Loading Event...</h1>
          </div>
        </section>
        <div className="import-container">
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="spinner" style={{ width: 40, height: 40, border: '3px solid #e5e7eb', borderTopColor: '#6B3A5B', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }}></div>
            <p>Loading event data...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="import-event-page">
        <section className="page-header">
          <div className="container">
            <div className="page-badge">EDIT EVENT</div>
            <h1 className="page-title">Error</h1>
            <p className="page-subtitle">{fetchError}</p>
          </div>
        </section>
        <div className="import-container">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <button className="btn-submit" onClick={() => navigate(-1)}>Go Back</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="import-event-page">
      <section className="page-header">
        <div className="container">
          <div className="page-badge">EDIT EVENT</div>
          <h1 className="page-title">Edit Event</h1>
          <p className="page-subtitle">Update the details for "{eventName}"</p>
        </div>
      </section>

      <div className="import-container">
        <div className="import-card">
          <form onSubmit={handleSubmit}>
            {/* Event Link Section */}
            <div className="event-link-section">
              <label className="form-label">Event Link</label>
              <div className="event-link-input">
                <input
                  type="url"
                  className={`form-control ${urlErrors.eventLink ? 'is-invalid' : ''}`}
                  value={eventLink}
                  onChange={(e) => setEventLink(e.target.value)}
                  placeholder="https://www.facebook.com/events/..."
                />
              </div>
            </div>

            {/* Basic Information */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-calendar-event"></i> Event Details</h3>

              <div className="mb-3">
                <label className="form-label">Event Name <span className="required">*</span></label>
                <input type="text" className="form-control" value={eventName}
                  onChange={(e) => setEventName(e.target.value)} placeholder="e.g., Bachata Stars Festival 2025" required />
              </div>

              <div className="date-time-row mb-3">
                <div>
                  <label className="form-label">Event Series <small className="text-muted">(if recurring)</small></label>
                  <div className="autocomplete-input-wrapper">
                    <input type="text" className="form-control" value={eventSeries}
                      onChange={handleEventSeriesInput}
                      onBlur={() => setTimeout(() => setEventSeriesSuggestions([]), 200)}
                      placeholder="e.g., Bachata Stars Festival" autoComplete="off" />
                    {eventSeriesSuggestions.length > 0 && (
                      <div className="autocomplete-suggestions show">
                        {eventSeriesSuggestions.map((series, idx) => (
                          <div key={idx} className="autocomplete-suggestion" onClick={() => selectEventSeries(series.name)}>
                            <span className="suggestion-name">{series.name}</span>
                            <span className="suggestion-count">{series.count} event{series.count !== 1 ? 's' : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="form-label">Edition Number</label>
                  <input type="number" className="form-control" value={edition}
                    onChange={(e) => setEdition(e.target.value)} placeholder="e.g., 5" min="1" />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Event Dates <span className="required">*</span></label>
                <div className="date-range-picker">
                  <div className="date-input-wrapper">
                    <input type="date" className="form-control" value={startDate}
                      onChange={(e) => setStartDate(e.target.value)} required />
                  </div>
                  <span className="date-separator">to</span>
                  <div className="date-input-wrapper">
                    <input type="date" className="form-control" value={endDate}
                      onChange={(e) => setEndDate(e.target.value)} required />
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Description <span className="required">*</span> <small>(minimum 20 words)</small></label>
                <textarea className="form-control" value={description}
                  onChange={(e) => setDescription(e.target.value)} rows="4"
                  placeholder="Describe the event..." required></textarea>
                <div className={`word-counter ${wordCount >= 20 ? 'success' : 'error'}`}>
                  {wordCount} / 20 words
                </div>
              </div>
            </div>

            {/* Location Section */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-geo-alt"></i> Location</h3>

              <div className="mb-3">
                <label className="form-label">Search Location <span className="required">*</span></label>
                <div className="autocomplete-input-wrapper">
                  <input type="text" className="form-control" value={locationSearch}
                    onChange={handleLocationInput}
                    onBlur={() => setTimeout(() => setLocationSuggestions([]), 200)}
                    placeholder="Start typing an address, venue, or city..." autoComplete="off" />
                  {locationSuggestions.length > 0 && (
                    <div className="autocomplete-suggestions show">
                      {locationSuggestions.map((loc, idx) => (
                        <div key={idx} className="autocomplete-suggestion" onClick={() => selectLocation(loc)}>
                          <span className="suggestion-name">
                            {loc.display_name.length > 60 ? loc.display_name.substring(0, 57) + '...' : loc.display_name}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Venue Name <span className="required">*</span></label>
                <div className="autocomplete-input-wrapper">
                  <input type="text" className="form-control" value={venueName}
                    onChange={handleVenueInput}
                    onBlur={() => setTimeout(() => setVenueSuggestions([]), 200)}
                    placeholder="e.g., Grand Ballroom Hotel" required autoComplete="off" />
                  {venueSuggestions.length > 0 && (
                    <div className="autocomplete-suggestions show">
                      {venueSuggestions.map((venue, idx) => (
                        <div key={idx} className="autocomplete-suggestion" onClick={() => selectVenue(venue)}>
                          <div>
                            <span className="suggestion-name">{venue.name}</span>
                            <div className="venue-location">{[venue.city, venue.country].filter(Boolean).join(', ')}</div>
                          </div>
                          <span className="suggestion-count">{venue.eventCount} event{venue.eventCount !== 1 ? 's' : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Venue Address</label>
                <input type="text" className="form-control" value={venueAddress}
                  onChange={(e) => setVenueAddress(e.target.value)} placeholder="Full street address" />
              </div>

              <div className="date-time-row mb-3">
                <div>
                  <label className="form-label">City <span className="required">*</span></label>
                  <input type="text" className="form-control" value={city}
                    onChange={(e) => setCity(e.target.value)} required />
                </div>
                <div>
                  <label className="form-label">Country <span className="required">*</span></label>
                  <input type="text" className="form-control" value={country}
                    onChange={(e) => setCountry(e.target.value)} required />
                </div>
              </div>
            </div>

            {/* Dance Styles Section */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-music-note-beamed"></i> Dance Styles <span className="required">*</span></h3>
              <div className="dance-styles-container">
                {selectedDanceStyles.map((style, idx) => (
                  <span key={idx} className="dance-style-tag">
                    {style}
                    <span className="remove-tag" onClick={() => removeDanceStyle(style)}>&times;</span>
                  </span>
                ))}
              </div>
              <div className="autocomplete-input-wrapper">
                <input type="text" className="form-control" value={danceStyleInput}
                  onChange={handleDanceStyleInput} onKeyDown={handleDanceStyleKeydown}
                  onBlur={() => setTimeout(() => setDanceStyleSuggestions([]), 200)}
                  placeholder="Start typing a dance style..." autoComplete="off" />
                {danceStyleSuggestions.length > 0 && (
                  <div className="autocomplete-suggestions show">
                    {danceStyleSuggestions.map((item, idx) => (
                      <div key={idx} className="autocomplete-suggestion" onClick={() => addDanceStyle(item.name)}>
                        <span className="suggestion-name">{item.name}</span>
                        <span className="suggestion-count">{item.count} event{item.count !== 1 ? 's' : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
                <small className="form-text text-muted">Press Enter to add. Use commas to add multiple.</small>
              </div>
            </div>

            {/* Event Image */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-image"></i> Event Image</h3>
              <div className="mb-3">
                <label className="form-label">Image URL</label>
                <input type="url" className={`form-control ${urlErrors.imageUrl ? 'is-invalid' : ''}`}
                  value={eventImageUrl} onChange={handleImageUrlChange}
                  placeholder="https://example.com/event-image.jpg" />
                {urlErrors.imageUrl && (
                  <small className="url-validation-error"><i className="bi bi-exclamation-circle"></i> {urlErrors.imageUrl}</small>
                )}
              </div>
              {eventImageUrl && !urlErrors.imageUrl && (
                <img src={eventImageUrl} alt="Event preview" className="image-preview"
                  style={{ display: 'block' }} onError={(e) => { e.target.style.display = 'none'; }} />
              )}
            </div>

            {/* Organizer & Price */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-person-badge"></i> Organizer & Pricing</h3>

              <div className="mb-3">
                <label className="form-label">Organizers <span className="required">*</span></label>
                <div className="organizers-container">
                  {selectedOrganizers.map((org, idx) => (
                    <span key={idx} className="organizer-tag">
                      {org}
                      <span className="remove-tag" onClick={() => removeOrganizer(org)}>&times;</span>
                    </span>
                  ))}
                </div>
                <div className="autocomplete-input-wrapper">
                  <input type="text" className="form-control" value={organizerInput}
                    onChange={handleOrganizerInput} onKeyDown={handleOrganizerKeydown}
                    onBlur={() => setTimeout(() => setOrganizerSuggestions([]), 200)}
                    placeholder="Start typing an organizer name..." autoComplete="off" />
                  {organizerSuggestions.length > 0 && (
                    <div className="autocomplete-suggestions show">
                      {organizerSuggestions.map((org, idx) => (
                        <div key={idx} className="autocomplete-suggestion" onClick={() => addOrganizer(org.name)}>
                          <span className="suggestion-name">{org.name}</span>
                          <span className="suggestion-count">{org.count} event{org.count !== 1 ? 's' : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <small className="form-text text-muted">Press Enter to add. Use commas to add multiple.</small>
                </div>
              </div>

              <div className="date-time-row mb-3">
                <div className="price-currency-group">
                  <label className="form-label">Price Range</label>
                  <div className="price-currency-inputs">
                    <select className="form-control currency-select" value={currency}
                      onChange={(e) => setCurrency(e.target.value)}>
                      {currencyOptions.map(opt => (
                        <option key={opt.code} value={opt.code}>{opt.label}</option>
                      ))}
                    </select>
                    <input type="text" className="form-control price-input" value={price}
                      onChange={(e) => setPrice(e.target.value)} placeholder="e.g., 150 - 300 or Free" />
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Ticket Purchase Link</label>
                <input type="url" className={`form-control ${urlErrors.ticketLink ? 'is-invalid' : ''}`}
                  value={ticketLink} onChange={(e) => setTicketLink(e.target.value)}
                  placeholder="https://tickets.example.com/event" />
              </div>

              <div className="mb-3">
                <label className="form-label">People Interested/Going (from FB)</label>
                <input type="text" className="form-control" value={peopleResponse}
                  onChange={(e) => setPeopleResponse(e.target.value)}
                  placeholder="e.g., 500 interested, 200 going" />
              </div>
            </div>

            {/* Artists Section */}
            <div className="form-section">
              <h3 className="form-section-title"><i className="bi bi-star"></i> Featured Artists / Instructors</h3>
              <div className="artists-container">
                {selectedArtists.map((artist, idx) => (
                  <span key={idx} className="artist-tag">
                    {artist}
                    <span className="remove-tag" onClick={() => removeArtist(artist)}>&times;</span>
                  </span>
                ))}
              </div>
              <div className="autocomplete-input-wrapper">
                <input type="text" className="form-control" value={artistInput}
                  onChange={handleArtistInput} onKeyDown={handleArtistKeydown}
                  onBlur={() => setTimeout(() => setArtistSuggestions([]), 200)}
                  placeholder="Start typing an artist name..." autoComplete="off" />
                {artistSuggestions.length > 0 && (
                  <div className="autocomplete-suggestions show">
                    {artistSuggestions.map((item, idx) => (
                      <div key={idx} className="autocomplete-suggestion" onClick={() => addArtist(item.name)}>
                        <span className="suggestion-name">{item.name}</span>
                        <span className="suggestion-count">{item.count} event{item.count !== 1 ? 's' : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
                <small className="form-text text-muted">Press Enter to add. Use commas to add multiple.</small>
              </div>
            </div>

            {/* Status Messages */}
            {statusMessage.text && (
              <div className={`status-message ${statusMessage.type}`}>
                {statusMessage.text}
              </div>
            )}

            {/* Submit Section */}
            <div className="submit-section">
              <button type="button" className="btn-cancel" onClick={() => navigate(-1)}>Cancel</button>
              <button type="submit" className="btn-submit" disabled={isSubmitting || !user}>
                {isSubmitting ? (
                  <><i className="bi bi-hourglass-split"></i> Saving Changes...</>
                ) : (
                  <><i className="bi bi-check-circle"></i> Save Changes</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditEventPage;
